import { ColumnType } from 'antd/lib/table'
import { useCallback } from 'react'
import { DateLike, toMoment, toMoments } from 'utils/toMoment'
import { FilterOptions, NumberRangeFilterValue } from '../types'
import { filterByKey } from '../utils/filterByKey'

type UseOnFilter<T> = (filter: FilterOptions<T>) => ColumnType<T>['onFilter']

/**
 * Creates a resolver that converts a table filter configuration into an Ant
 * Design `onFilter` predicate.
 */
export function useOnFilter<T>() {
  return useCallback<UseOnFilter<T>>((filter) => {
    if (filter.type === 'search') {
      if (filter.with === 'callback') return filter.onFilter
      return filterByKey(filter)
    } else if (filter.type === 'date')
      return (value, record) => {
        const [from, to] = toMoments(value.toString().split(',')) ?? []
        const date =
          filter.with === 'value'
            ? filter.value(record)
            : (record[filter.key] as unknown as DateLike)
        const moment = toMoment(date)
        return (
          moment?.isBetween(from?.startOf('day'), to?.endOf('day')) ?? false
        )
      }
    else if (filter.type === 'boolean') {
      if (filter.with === 'callback') return filter.onFilter
      else if (filter.with === 'key') return filterByKey(filter)
      return (value, record) => {
        if (filter.skip?.(value, record)) return false
        const checked = filter.value(record)
        if (filter.strict && value === false && checked === undefined)
          return false
        return value === (checked ?? false)
      }
    } else if (filter.type === 'checkboxes')
      return (value, record) => {
        const parsed = JSON.parse(value.toString()) as {
          value: string | number | boolean
          checked: boolean
        }
        if (filter.skip?.(parsed.value, record)) return false
        const checked = filter.value(parsed.value, record)
        if (filter.strict && parsed.checked === false && checked === undefined)
          return false
        return parsed.checked === (checked ?? false)
      }
    else if (filter.type === 'number')
      return (value, record) => {
        const { greater, less } = JSON.parse(
          value.toString()
        ) as NumberRangeFilterValue
        const num =
          filter.with === 'value'
            ? filter.value(record)
            : (record[filter.key] as number)
        if (typeof num !== 'number') return false
        else if (typeof greater === 'number' && typeof less === 'number')
          return num >= greater && num <= less
        else if (typeof greater === 'number') return num >= greater
        else if (typeof less === 'number') return num <= less
        return false
      }
    else if (filter.with === 'callback') return filter.onFilter
    else return filterByKey(filter)
  }, [])
}
